/* eslint-disable react/jsx-no-bind */
import React, { useState, useRef, useContext, useEffect } from 'react';
import { bisector } from 'd3-array';
import { axisBottom, axisRight } from 'd3-axis';
import { scaleTime, scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { line } from 'd3-shape';
import { timeYear } from 'd3-time';
import { object, number, array, string, exact } from 'prop-types';
import { HoverContext } from './context';
import ToolTip from './tooltip';

function chart( props ) {

    /* States and refs */
    const chartData = props.data;

    // tooltip data is just derived from prop data, no need for state
    const [ hoveredData, setHoveredData ] = useState( props.data[0] );
    const [ xAxisYearsGap, setXAxisYearsGap ] = useState( 2 );

    const svgRef = useRef();
    const axisXRef = useRef();
    const axisYRef = useRef();
    const lineRef = useRef();
    const scrubberRef = useRef();
    const dotRef = useRef();

    const isLVP = props.vps ? props.vps.isLVP : false;

    const { setHover } = useContext( HoverContext );

    const compWrapOffsetLeft = props.componentWrapper ? props.componentWrapper.offsetLeft : 0;
    const compWrapOffsetTop = props.componentWrapper ? props.componentWrapper.offsetTop : 0;
    const compWrapOffsetWidth = props.componentWrapper ? props.componentWrapper.offsetWidth : 0;
    const pageOffsetTop = props.pageWrapper ? props.pageWrapper.offsetTop : 0;

    const isPercent = props.yAxisTitle[0] === 'Percent';

    // Operation that scales x values of the data
    const widthForScale = compWrapOffsetWidth - props.margin.left - ( isLVP ? 0 : props.margin.right );
    const heightForScale = props.height - props.margin.top - props.margin.bottom;

    /* Defines the domains */
    const getXDomain = ( selectedDateRange ) => {

        const xDomain = {
            start: selectedDateRange[0].column.date,
            end: selectedDateRange[selectedDateRange.length - 1].column.date
        };

        return xDomain;

    };

    const { start, end } = getXDomain( chartData );

    const x = scaleTime()
        .domain( [ start, end ] )
        .range( [ 0, widthForScale ] );

    const y = scaleLinear().domain( [ props.yMin, props.yMax ] )
        .range( [ heightForScale, 0 ] )
        .nice();

    let xVal = 0;

    let yVal = 0;

    let d = {};

    useEffect( () => {

        if ( props.vps.isSVP ) setXAxisYearsGap( 4 );
        else setXAxisYearsGap( 2 );

    }, [ props.vps.isSVP ] );

    const renderXAxis = () => {

        // Creates the x-axis path
        const xAxisPath = axisBottom( x );

        // Selects where the axis will be injected
        const g = select( axisXRef.current );

        g.call( xAxisPath.tickSizeInner( 6 ).ticks( timeYear.every( xAxisYearsGap ) )
            .tickFormat( ( d ) => d.getFullYear() ) );

        // Label only certain points
        g.select( '.domain' )
            .attr( 'stroke', '#515c70' )
            .attr( 'stroke-width', '1.5px' );

        g.selectAll( '.tick line' )
            .attr( 'stroke', '#515c70' )
            .attr( 'stroke-width', '1.5px' );

        g.selectAll( '.tick text' )
            .attr( 'y', 22 )
            .attr( 'fill', '#515c70' )
            .attr( 'font-size', '12px' )
            .attr( 'font-family', 'Graphik-Medium' )
            .attr( 'class', '' );

        g.select( '.tick:last-of-type text' )
            .attr( 'x', -5 );

        g.selectAll( '.tick text' ).attr( 'fill', '#515c70' )
            .attr( 'font-size', '12px' )
            .attr( 'font-family', 'Graphik-Medium' );

        // Transformation to move the axis down
        const translate = `translate(0,${ props.height - props.margin.top - props.margin.bottom })`; // this moves it down

        // Returns the html for rendering the x-axis
        return (
            <g
                className='x-axis'
                ref={ axisXRef }
                transform={ translate }
            ></g>
        );

    };

    const renderYAxis = () => {

        const yAxisPath = axisRight( y )
            .tickSize( props.width )
            .tickFormat( ( d, i, n ) => {

                return isPercent ? `${ d }%` : `$${ d / 1000 }`;

            } )
            .ticks( 6 );

        const g = select( axisYRef.current );


        g.call( yAxisPath );

        g.select( '.domain' ).remove();
        g.selectAll( '.tick text' )
            .attr( 'dy', 3.5 )
            .attr( 'fill', '#515c70' )
            .attr( 'font-size', '12px' )
            .attr( 'font-family', 'Graphik-Medium' )
            .attr( 'x', ( _, i, n ) => {

                const { width } = n[i].getBBox();


                return -width - 5;

            } );

        g.selectAll( '.tick line' )
            .attr( 'stroke-dasharray', ( d ) => ( d === 0 ? '0' : '4' ) )
            .attr( 'stroke', '#929fb3' )
            .attr( 'x2', compWrapOffsetWidth - props.margin.left - ( isLVP ? 0 : props.margin.right ) );

        return <g className='y-axis' ref={ axisYRef }></g>;

    };

    const renderLine = () => {

        if ( svgRef.current ) {

            const lineGenerator = line()
                .defined( ( d ) => d.value !== null && d.value !== undefined )
                .x( ( d ) => x( d.date ) )
                .y( ( d ) => y( d.value ) );

            const rows = chartData[0].rows;

            // for each row that is supposed to be shown
            // get associated colorData, date and value for that date
            const lineData = rows.map( ( row ) => {

                return (
                    {
                        id: row.name,
                        color: row.color,
                        values: chartData.map( ( data ) => {

                            return (
                                {
                                    date: data.column.date,
                                    value: ( data.rows.filter( ( obj ) => {

                                        if ( obj.name === row.name ) return obj;

                                    } ) )[0].value

                                } );

                        } )

                    } );

            } );

            const lines = lineData.map( ( lineObj ) => {

                return (
                    <path
                        className='line'
                        fill='none'
                        stroke={ lineObj.color }
                        strokeWidth={ 4 }
                        key={ lineObj.id + lineObj.color }
                        d={ lineGenerator( lineObj.values ) }
                    />
                );

            } );

            return lines;

        }

    };

    const updateTooltip = ( year, month ) => {

        const currData = props.data.find( ( datapoint ) => datapoint.column.date.getFullYear() === year && datapoint.column.date.getMonth() === month );

        // checks if currData is not empty (meaning found the matching data point)
        if ( currData ) {

            setHoveredData( currData );

        }

    };

    const renderHoverDots = () => {

        return hoveredData.rows.map( ( { value }, i ) => {

            return (
                <circle
                    r='7'
                    opacity={ 0 }
                    key={ i }
                    strokeWidth={ 2 }
                    className={ `dot-per-line ${ value === undefined ? 'dot-per-line-hidden' : '' }` }
                />
            );

        } );

    };

    const handleSliderHover = ( e ) => {

        let xOffset = 0;

        let yOffset = 0;

        // this chart is the one being hovered, not the other
        xOffset = e.pageX - props.pageWrapper.offsetLeft - compWrapOffsetLeft;

        // pagey, componentoffsettop, parentoffsettop, heights of titles/legend titles, tooltip height, constant
        yOffset = e.pageY - compWrapOffsetTop - pageOffsetTop - 280 - 20;

        xVal = xOffset - props.margin.left;
        yVal = yOffset;

        // mapping the mouse position to what value corresponds on the chart
        const date = x.invert( Math.abs( xVal ) );

        // d3 bisector is used to help figure out what value the mouse is currently/or nearest to pointing to? (https://observablehq.com/@d3/d3-bisect)
        const bisectDate = bisector( function ( da ) {

            return da.column.date;

        } ).left;

        // this section is looking for the date from the dataset which is nearest to the mouse
        const i = bisectDate( chartData, date, 1 );

        const d0 = chartData[i - 1];
        const d1 = chartData[i];
        const daa = ( d1 === undefined ? d0 : ( date - d0.column.date > d1.column.date - date ? d1 : d0 ) );

        d = daa;

        if ( !d ) return;

        updateTooltip( d.column.date.getFullYear(), d.column.date.getMonth() );

        setHover( {
            xVal: xVal,
            yVal: yVal,
            d: [ d.column.date.getFullYear(), d.column.date.getMonth() ],
            lines: lineRef.current.childNodes
        } );

    };

    const handleSliderOut = ( _ ) => {

        setHover( { xVal: undefined, yVal: undefined, d: undefined } );

        const mouseDots = Array.from( document.getElementsByClassName( 'dot-per-line' ) );

        mouseDots.forEach( ( hoverDot ) => {

            hoverDot.setAttribute( 'opacity', 0 );

        } );

    };

    const isDataZero = ( prefix ) => {

        const prefixData = props.data.map( ( d ) => d.rows.filter( ( row ) => row.name.includes( prefix ) ) );

        return prefixData.every( ( pd ) => pd[0].value === 0 );

    };

    const renderLegend = () => {

        return (
            <div className='chart-legend'>
                { props.legend.map( ( leg, idx ) => {

                    return (
                        <div key={ `${ leg + idx }1` } className='chart-legend-group'>
                            <p>{ leg['title'] }</p>

                            <div className='chart-legend-group-sub'>
                                { leg['sub'].map( ( sub, subIdx ) => {

                                    return (
                                        <p key={ `${ sub + subIdx }1` }>
                                            { sub.title }
                                            { sub.color && ( isDataZero( leg['prefix'] ) || sub.hasData )
                                                ? <span style={{ backgroundColor: sub.color }} />
                                                : <span className='no-data'>No data available</span>
                                            }
                                        </p>
                                    );

                                } ) }
                            </div>
                        </div>
                    );

                } ) }
            </div>
        );

    };

    const renderDots = () => chartData.map( ( point, i ) => {

        const { value, color } = point.rows[0];


        if ( value !== null && value !== undefined ) {

            return (
                <circle
                    className='dot'
                    fill={ color }
                    stroke={ color }
                    strokeWidth={ 2 }
                    key={ i }
                    cx={ x( point.column.date ) }
                    cy={ y( value ) }
                    r={ 1 }
                />
            );

        }

    } );

    const tooltipProps = {
        label: props.label,
        isLVP: isLVP,
        data: chartData,
        multipleCharts: props.multipleCharts,
        chartNo: props.chartNo,
        yEndVal: props.height - props.margin.top - props.margin.bottom,
        getX: x,
        getY: y,
        widthForScale: widthForScale,
        margin: props.margin
    };

    let calcWidth = 0;


    if ( props.componentWrapper === undefined ) {

        calcWidth = props.width;

    }
    else {

        calcWidth = props.componentWrapper.offsetWidth;

    }

    return (
        // confirm that at least one sub in legend has data. if none of them do, don't render the chart
        // just show the text saying there's no data.
        props.legend.some( ( l ) => l['sub'][0].hasData )
            ? <div className={ `data-visualization-section ${ props.chartNo }` }>
                <div style={{ top: `${ props.margin.top - ( props.yAxisTitle[0] === 'Percent' ? 30 : 40 ) }px` }} className='y-axis-title-container'>
                    <p className='y-axis-title'>{ `${ props.yAxisTitle[0].toUpperCase() } ${ props.yAxisTitle[0] === 'Percent' ? '(%)' : '($)' }` }</p>
                    { props.yAxisTitle.length > 1 ? <p className='y-axis-subtitle'>({ props.yAxisTitle[1] })</p> : '' }
                </div>
                <svg ref={ svgRef } width={ props.width - props.margin.left - props.margin.right } height={ props.height } >
                    <g height={ 500 } transform={ `translate(${ props.margin.left }, ${ props.margin.top })` }>
                        { renderYAxis() }
                        { renderXAxis() }
                        <g className='dot-render' ref={ dotRef }>
                            { renderDots() }
                        </g>
                        <g className='line-render' ref={ lineRef }>
                            { renderLine() }
                        </g>
                        <g className='scrubber-section'>
                            <path className='scrubber' ref={ scrubberRef }></path>
                            { renderHoverDots() }
                            <rect
                                className='mouse-over-canvas'
                                id={ props.chartNo }
                                fill='none'
                                pointerEvents='all'
                                height={ props.height - props.margin.top - props.margin.bottom }
                                width={ calcWidth - props.margin.left }
                                onMouseMove={ ( e ) => handleSliderHover( e ) }
                                onMouseOut={ ( e ) => handleSliderOut( e ) }
                            />
                        </g>
                    </g>
                </svg>
                <div className='x-axis-title'>
                    <p>Year</p>
                </div>
                { renderLegend() }
                { <ToolTip tooltipProps={ tooltipProps } isPercent={ isPercent } /> }
            </div>
            : <p className='no-chart-data-text'>No data available for this combination. Please try different selections.</p>
    );

}

chart.propTypes = {
    data: array.isRequired,
    rawData: array.isRequired,
    margin: exact( { top: number, right: number, bottom: number, left: number } ),
    height: number.isRequired,
    width: number.isRequired,
    title: string.isRequired,
    label: array.isRequired,
    colors: array.isRequired,
    yAxisTitle: array.isRequired,
    componentWrapper: object,
    startDate: number,
    endDate: number,
    vps: object
};

export default chart;